import { useQuery } from '@tanstack/react-query';
import { api } from '../api/cliente.js';
import { AcessoNegado, Carregando, ErroSeguro, Vazio } from '../componentes/estados.js';
import { useSessao } from '../sessao.js';

/**
 * Categorias e Auditoria.
 *
 * Ambas são telas de leitura nesta entrega: a Auditoria é imutável por
 * definição, e o CRUD de categorias entra junto com os relatórios, quando a
 * edição de palavras-chave passa a ter efeito visível no acerto do parser.
 */

export function Categorias() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => api.categorias(),
  });

  if (isLoading) return <Carregando />;
  if (isError || !data) return <ErroSeguro aoTentarNovamente={() => void refetch()} />;

  const entradas = data.categorias.filter((c) => c.tipo === 'entrada');
  const saidas = data.categorias.filter((c) => c.tipo === 'saida');

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-[length:var(--text-tela)] font-semibold text-texto">Categorias</h1>
        <p className="mt-0.5 text-[length:var(--text-secundario)] text-texto-secundario">
          As palavras-chave de cada categoria alimentam o reconhecimento automático dos lançamentos.
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <Grupo titulo="Entradas" categorias={entradas} />
        <Grupo titulo="Saídas" categorias={saidas} />
      </div>
    </div>
  );
}

function Grupo({
  titulo,
  categorias,
}: {
  titulo: string;
  categorias: Array<{ id: string; nome: string; cor: string }>;
}) {
  return (
    <section className="rounded-[var(--radius-card)] border border-borda bg-superficie shadow-[var(--shadow-card)]">
      <h2 className="border-b border-borda-sutil px-4 py-3 text-[length:var(--text-coluna)] font-semibold uppercase tracking-[0.05em] text-texto-terciario">
        {titulo} ({categorias.length})
      </h2>
      <ul>
        {categorias.map((c) => (
          <li
            key={c.id}
            className="flex items-center gap-2.5 border-b border-borda-sutil px-4 py-2.5 last:border-b-0"
          >
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ backgroundColor: c.cor }}
              aria-hidden
            />
            <span className="text-[length:var(--text-corpo)] text-texto">{c.nome}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

const ROTULO_ACAO: Record<string, { texto: string; classe: string }> = {
  login: { texto: 'LOGIN', classe: 'bg-neutro-bg text-neutro-fg border-neutro-borda' },
  logout: { texto: 'LOGOUT', classe: 'bg-neutro-bg text-neutro-fg border-neutro-borda' },
  criou: { texto: 'CRIOU', classe: 'bg-entrada-bg text-entrada-fg border-entrada-borda' },
  alterou: { texto: 'ALTEROU', classe: 'bg-pendente text-white border-pendente-borda' },
  excluiu: { texto: 'EXCLUIU', classe: 'bg-saida-bg text-saida-fg border-saida-borda' },
  confirmou: { texto: 'CONFIRMOU', classe: 'bg-entrada-bg text-entrada-fg border-entrada-borda' },
  descartou: { texto: 'DESCARTOU', classe: 'bg-saida-bg text-saida-fg border-saida-borda' },
  conectou_whatsapp: {
    texto: 'CONECTOU',
    classe: 'bg-primaria-bg text-primaria-fg border-primaria-borda',
  },
  desconectou_whatsapp: {
    texto: 'DESCONECTOU',
    classe: 'bg-saida-bg text-saida-fg border-saida-borda',
  },
};

export function Auditoria() {
  const { ehAdmin } = useSessao();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['auditoria'],
    queryFn: () => api.auditoria(100),
    enabled: ehAdmin,
  });

  // O servidor recusaria de qualquer forma; esconder aqui evita o erro feio.
  if (!ehAdmin) return <AcessoNegado />;
  if (isLoading) return <Carregando />;
  if (isError || !data) return <ErroSeguro aoTentarNovamente={() => void refetch()} />;

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-[length:var(--text-tela)] font-semibold text-texto">Auditoria</h1>
        <p className="mt-0.5 text-[length:var(--text-secundario)] text-texto-secundario">
          Trilha imutável. Nem o sistema consegue alterar ou apagar o que está aqui.
        </p>
      </header>

      {data.registros.length === 0 ? (
        <Vazio
          titulo="Nada registrado ainda"
          orientacao="As ações aparecem aqui conforme acontecem."
        />
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-borda bg-superficie shadow-[var(--shadow-card)]">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-borda-sutil text-left">
                {['Data', 'Ator', 'Ação', 'Entidade', 'Motivo'].map((h) => (
                  <th
                    key={h}
                    className="px-3 py-2.5 text-[length:var(--text-coluna)] font-semibold uppercase tracking-[0.05em] text-texto-terciario"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.registros.map((r) => {
                const acao = ROTULO_ACAO[r.acao] ?? {
                  texto: r.acao.toUpperCase(),
                  classe: 'bg-neutro-bg text-neutro-fg border-neutro-borda',
                };
                return (
                  <tr key={r.id} className="border-b border-borda-sutil last:border-b-0">
                    <td className="mono whitespace-nowrap px-3 py-2.5 text-[length:var(--text-metadado)] text-texto-terciario">
                      {new Date(r.criadoEm).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-3 py-2.5 text-[length:var(--text-secundario)] text-texto">
                      {r.atorNome ?? 'sistema'}
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`whitespace-nowrap rounded-[var(--radius-badge)] border px-2 py-0.5 text-[10px] font-semibold ${acao.classe}`}
                      >
                        {acao.texto}
                      </span>
                    </td>
                    <td className="mono px-3 py-2.5 text-[length:var(--text-metadado)] text-texto-secundario">
                      {r.entidade}
                    </td>
                    <td className="px-3 py-2.5 text-[length:var(--text-secundario)] text-texto-secundario">
                      {r.motivo ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Placeholder honesto para o que ainda não existe, em vez de rota quebrada. */
export function EmBreve({ titulo, descricao }: { titulo: string; descricao: string }) {
  return (
    <div>
      <header className="mb-4">
        <h1 className="text-[length:var(--text-tela)] font-semibold text-texto">{titulo}</h1>
      </header>
      <Vazio titulo="Ainda não disponível" orientacao={descricao} />
    </div>
  );
}
